import classNames from 'classnames';
import { autobind } from 'core-decorators';
import PropTypes from 'prop-types';
import { Component } from 'react';
// V3 components
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { isMobile } from '/@/utils';
import { reverse } from '/@/utils/router';
import CatalogHeader from '/@/v3/catalog/catalog-header';
import Notes from '/@/v3/components/notes';
import Title from '/@/v3/components/title';
import CatalogProductShape from '/@/views/catalog/product-selection/old-product';

import bugsCover from './assets/covers/bugs.png';
import pathogensCover from './assets/covers/pathogens.png';
import styles from './styles.module.css';

@autobind
class Acceleron extends Component {
    static contextTypes = {
        history: PropTypes.object,
    };

    appInstance = settings.app;

    handleGoBack() {
        this.context.history(
            reverse('catalog.ProductSelection', {
                params: { type: 'corn' },
            })
        );
    }

    renderHeader() {
        const brand = {
            logoCodename: 'acceleron',
            theme: 'acceleron',
            type: 'crop-protection',
        };

        return <CatalogHeader brand={brand} onGoBack={this.handleGoBack} />;
    }

    renderSeedTreatment(cultivioNotes) {
        return (
            <div className={styles.seedTreatment}>
                <div className={styles.header}>
                    <Title color='secondary' bolder>
                        {_('¿A qué llamamos tratamientos de semilla?')}
                    </Title>
                </div>

                <div className={styles.body}>
                    <Title size='md'>
                        {_(
                            'Es la aplicación de ingredientes químicos, organismos biológicos y/o nutrientes directamente sobre las mismas y antes de la siembra, para promover el crecimiento ante condiciones adversas, controlar o repeler patógenos, insectos u otras plagas que atacan a las semillas y plántulas de diferentes especies.'
                        )}
                        <br />
                        <br />
                        {_(
                            'Es un pilar que combina todas las tecnologías de protección del cultivo: insecticidas + fungicidas + biológicos. Por lo tanto, este pilar requiere de intensos análisis de compatibilidad entre los productos como también de evaluaciones sobre nuestro germoplasma elite. BAYER cuenta con una robusta red de análisis para evaluar la compatibilidad y efectos en su germoplasma elite, seleccionando la mejor combinación que asegura un tratamiento de semilla de excelente calidad.'
                        )}
                    </Title>

                    <br />

                    <Title size='md' bolder>
                        {_('El tratamiento de semilla brinda un control preventivo y potenciador que permite:')}
                    </Title>
                </div>

                <div className={styles.features}>
                    <Notes notes={cultivioNotes.featuresNotes} />
                </div>
            </div>
        );
    }

    renderFactors() {
        return (
            <div className={styles.factors}>
                <Title color='primary' size={isMobile() ? 'xl' : 'xxl'} align={isMobile() ? 'left' : 'right'} bolder>
                    {_('Principales factores abióticos que afectan la emergencia y el establecimiento')}
                </Title>

                <div className={styles.list}>
                    <div className={styles.item}>
                        <Title size='lg' bolder>
                            {_('Disponibilidad de nutrientes')}
                        </Title>
                        <Title size='md'>
                            {_(
                                'En los suelos podemos encontrar nutrientes en formas no disponibles para el cultivo o bien en deficiencia.'
                            )}
                            {_('Ejemplos comunes se dan para el caso de Zinc, Boro, Molibdeno, Nitrógeno y Fósforo.')}
                        </Title>
                    </div>

                    <div className={styles.item}>
                        <Title size='lg' bolder>
                            {_('Disponibilidad de agua')}
                        </Title>
                        <Title size='md'>
                            {_(
                                'La estructura del suelo y el desarrollo de las raíces pueden jugar un rol fundamental en la disponibilidad y captación del agua.'
                            )}
                        </Title>
                    </div>

                    <div className={styles.item}>
                        <Title size='lg' bolder>
                            {_('Profundidad de siembra y compactación del suelo')}
                        </Title>
                        <Title size='md'>{_('Produce un lote des-uniforme y/o con pobre desarrollo radicular.')}</Title>
                    </div>
                </div>
            </div>
        );
    }

    renderValueProposal() {
        return (
            <div className={styles.valueProposalRoot}>
                <div className={styles.content}>
                    <Title color='secondary' bolder>
                        {_('Propuesta de valor')}
                    </Title>
                    <Title size='md'>
                        {_(
                            'A través de los años hemos testeado Acceleron contra distintas tecnologías y pudimos comprobar una '
                        )}
                        <b>{_('mejora positiva de rendimiento entre 3% a 15%')}</b>
                        {_(' dependiendo de la zona, híbrido y campaña.')}
                        <br />
                        <br />
                        {_(
                            'Hoy continuamos validando este tratamiento y adicionándole tecnologías. Un diferencial de Acceleron en Dekalb es ingrediente'
                        )}{' '}
                        <b>{_('LCO')}</b>.
                        <br />
                        <br />
                        {_('El')} <b>{_('LCO')}</b>{' '}
                        {_(
                            'es un compuesto natural de comunicación rizosférica entre microorganismos y plantas. Mejorador del crecimiento temprano del cultivo, a través de mitigar estrés abiótico en implantación.'
                        )}
                        {_('El agregado de')} <b>{_('LCO')}</b>{' '}
                        {_(
                            'proporciona un incremento adicional en Acceleron del 2,2% por sobre los tratamientos de protección.'
                        )}
                    </Title>
                </div>
            </div>
        );
    }

    renderQuimicalComponents() {
        return (
            <div
                className={classNames(styles.quimicalComponents, {
                    [styles.slim]: this.appInstance.theme === 'latijereta',
                })}
            >
                <div className={styles.head}>
                    <Title color='primary' align='center' size='xl' bolder>
                        {_('¿Qué contiene Acceleron?')}
                    </Title>
                </div>

                <div className={styles.table}>
                    <div className={styles.head}>
                        <div className={styles.column} />

                        <div className={styles.column}>{_('Tipo activo')}</div>

                        <div className={styles.column}>{_('Grupo químicos')}</div>

                        <div className={styles.column}>{_('Modo de acción')}</div>
                    </div>

                    <div className={styles.body}>
                        <div className={styles.row}>
                            <div className={classNames(styles.block, styles.title)}>{_('QUÍMICOS')}</div>
                            <div className={styles.block}>
                                <div className={styles.innerBlock}>•{_('Insecticida Sistémico')}</div>
                                <div className={classNames(styles.innerBlock, styles.triple)}>•Fungicida</div>
                            </div>
                            <div className={styles.block}>
                                <div className={styles.innerBlock}>•{_('Neonicotinoide')}</div>
                                <div className={styles.innerBlock}>•{_('Estrobirulina')}</div>
                                <div className={styles.innerBlock}>•{_('Acilalaninas o fenilamidas')}</div>
                                <div className={styles.innerBlock}>•{_('Create Pro Max')}</div>
                            </div>
                            <div className={styles.block}>
                                <div className={styles.innerBlock}>
                                    •{_('Contacto y estomacal. Sistémico ascendente')}
                                </div>
                                <div className={styles.innerBlock}>•{_('Contacto')}</div>
                                <div className={classNames(styles.innerBlock, styles.double)}>•{_('Sistémicos')}</div>
                            </div>
                        </div>

                        {this.appInstance.theme !== 'latijereta' && (
                            <div className={styles.row}>
                                <div className={classNames(styles.block, styles.title)}>{_('BIOLÓGICOS')}</div>
                                <div className={styles.block}>
                                    <div className={styles.innerBlock}>•{_('Biológico')}</div>
                                </div>
                                <div className={styles.block}>
                                    <div className={styles.innerBlock}>•{_('No')}</div>
                                </div>
                                <div className={styles.block}>
                                    <div className={styles.innerBlock}>
                                        •{_('Favorece la simbiosis con las micorrizas.')}
                                    </div>
                                    <div className={classNames(styles.innerBlock, styles.double)}>
                                        •
                                        {_(
                                            'Favorece descomposición saprófita de materiales orgánicos, solubilización de inorgánicos, promueve el crecimiento vegetal.'
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className={styles.paper} />
            </div>
        );
    }

    renderAsurance() {
        return (
            <div className={styles.asurance}>
                <Title size='xl' align='center' bolder>
                    {_('Acceleron asegura')}
                </Title>

                <div className={styles.content}>
                    {this.appInstance.theme !== 'latijereta' && (
                        <div className={styles.box}>
                            <Title size='md'>
                                <strong>{_('El agregado de LCO')}</strong>{' '}
                                {_(
                                    'en el Acceleron demostró reducción en la proporción promedio de plantas “chicas” evaluadas en V4 y más plantas logradas, con mayor vigor y mejora de la uniformidad'
                                )}
                                {_(
                                    'Incrementa el vigor y el crecimiento del maíz en los estadios iniciales, impactando directamente en el rendimiento.'
                                )}
                            </Title>
                        </div>
                    )}

                    <div className={styles.box}>
                        <Title size='md'>
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: _(
                                        '<strong>Protección Superior frente a los principales patógenos</strong> de las semillas (<span style="font-style: italic;">Alternaria spp., Fusarium spp., Cladosporium spp., Aspergillus spp., Penicillium spp., Diplodia maydis</span>) y suelo (<span style="font-style: italic;">Rhizoctonia spp., Pythium spp., Fusarium spp.</span>) debido a su exclusiva combinación de una Estrobilurina, un Triazol y Metalaxyl con dosis ajustada para las situaciones más difíciles.'
                                    ),
                                }}
                            />
                        </Title>
                    </div>

                    <div className={styles.box}>
                        <Title size='md'>
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: _(
                                        '<strong>Protección frente a los principales insectos</strong> que afectan al cultivo en implantación. Gusanos blancos (<span style="font-style: italic;">Diloboderus abderus, Cyclocephala spp., Maecolaspis sp., Colaspis spp.</span>), Gusano alambre _(<span style="font-style: italic;">Dyscinetus gagates, Agriotes spp., Conoderus sp.</span>), Hormigas podadoras* _(<span style="font-style: italic;">Acromyrmex spp.</span>), Oruga cortadora** _(<span style="font-style: italic;">Agrotis spp.</span>) gracias a su neonicotinoide de última generación. Reduce el desprendimiento de polvo, mejorando la plantabilidad y fluidez a través de la incorporación de un nuevo polímero de cobertura.'
                                    ),
                                }}
                            />
                        </Title>
                    </div>
                </div>
            </div>
        );
    }

    renderNotes(laTijeretaNotes, cultivioNotes) {
        const notes = this.appInstance.theme === 'latijereta' ? laTijeretaNotes.footerNotes : cultivioNotes.footerNotes;

        return (
            <div className={styles.notes}>
                <Notes notes={notes} />
            </div>
        );
    }

    handleItemFactor(factor) {
        this.context.history(
            reverse('catalog.AcceleronFactors', {
                params: { id: factor },
            })
        );
    }

    renderImpact() {
        if (this.appInstance.theme === 'agroceres' || this.appInstance.theme === 'agroeste') return null;

        const pathogens = {
            profile: {
                product: {
                    catalogImage: pathogensCover,
                    catalogName: _('Enfermedades causadas por patógenos en la región'),
                },
                id: 'pathogens',
            },
            brand: {
                theme: 'acceleron',
                type: 'crop-protection',
            },
            color: 'dark',
            sizeTitle: 'lg',
            onClickProduct: this.handleItemFactor,
        };

        const bugs = {
            profile: {
                product: {
                    catalogImage: bugsCover,
                    catalogName: _('Insectos que impactan en el establecimiento del cultivo'),
                },
                id: 'bugs',
            },
            brand: {
                theme: 'acceleron',
                type: 'crop-protection',
            },
            color: 'dark',
            sizeTitle: 'lg',
            onClickProduct: this.handleItemFactor,
        };

        return (
            <div className={styles.impact}>
                <Title color='secondary' size='xl' bolder>
                    {_('Principales factores que impactan')}
                </Title>

                <div className={styles.list}>
                    <CatalogProductShape {...pathogens} />
                    <CatalogProductShape {...bugs} />
                </div>
            </div>
        );
    }

    render() {
        const cultivioNotes = {
            header: _(
                'Desde emergencia, hasta las primeras 4 hojas, el cultivo de maíz está expuesto a plagas, enfermedades y factores abióticos que pueden afectar la densidad y uniformidad del cultivo causándole la muerte y reduciendo su vigor. El tratamiento de semilla Acceleron® ayuda a maximizar la emergencia de plántulas, mejora la uniformidad temporal, mayor vigor radicular y aéreo. Provee además protección frente a enfermedades y contra el daño y la actividad de insectos. Acceleron® Potencia el rendimiento.'
            ),

            featuresNotes: [
                _(
                    'Afrontar factores de riesgo como enfermedades e insectos, los cuales pueden afectar el embrión y por lo tanto el desarrollo de la futura planta.'
                ),
                _(
                    'Facilitar la disponibilidad de nutrientes y estimular el crecimiento temprano para lograr una mejor implantación.'
                ),
            ],

            footerNotes: [
                _('Maximiza la emergencia de plántulas'),
                _('Mejora la uniformidad temporal'),
                _('Mayor vigor radicular y aéreo'),
                _('Protección frente a enfermedades'),
                _('Protección del daño y la actividad de insectos'),
                _('Nutrición: directa o indirecta'),
                _('Potencia el Rendimiento'),
            ],
        };

        const laTijeretaNotes = {
            header: _(
                'Desde emergencia, hasta las primeras 4 hojas, el cultivo de maíz está expuesto a plagas, enfermedades y factores abióticos que pueden afectar la densidad y uniformidad del cultivo causándole la muerte y reduciendo su vigor.\nEl tratamiento de semilla Acceleron® ayuda a mejorar la salud de las plantas en este estadio a través del control de enfermedades, la protección contra insectos y la mejora en el vigor y la densidad del cultivo, traduciéndose esto en mayor Potencial de Rendimiento.'
            ),

            featuresNotes: [
                _(
                    'Afrontar factores de riesgo como enfermedades e insectos, los cuales pueden afectar el embrión y por lo tanto el desarrollo de la futura planta.'
                ),
                _(
                    'Facilitar la disponibilidad de nutrientes y estimular el crecimiento temprano para lograr una mejor implantación.'
                ),
            ],

            footerNotes: [
                _('Maximiza la emergencia de plántulas.'),
                _('Protección frente a enfermedades.'),
                _('Protección del daño y la actividad de insectos.'),
            ],
        };

        return (
            <div className={styles.root}>
                {this.renderHeader()}
                <div className={styles.general}>
                    <Title color='secondary' size='xl' bolder>
                        Acceleron
                    </Title>
                    <Title bolder size='md' className={styles.subtitle}>
                        {this.appInstance.theme === 'latijereta' ? laTijeretaNotes.header : cultivioNotes.header}
                    </Title>
                </div>
                {this.renderSeedTreatment(cultivioNotes)}
                {this.renderFactors()}
                {this.appInstance.theme !== 'latijereta' && this.renderValueProposal()}
                {this.renderQuimicalComponents()}
                {this.renderAsurance()}
                {this.renderNotes(laTijeretaNotes, cultivioNotes)}
                {this.renderImpact()}

                <div
                    className={classNames(styles.footer, { [styles.slim]: this.appInstance.theme === 'latijereta' })}
                />
            </div>
        );
    }
}

export default Acceleron;
